import React, { useRef, useMemo, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks'
import useStyles from './styles'

const MultiSelectedTree = ({ data, categories, onSelectedItemsChange, onlyParent = false }) => {
  const styles = useStyles()
  const toggledItemRef = useRef({})
  const apiRef = useTreeViewApiRef()
  const [selectedItems, setSelectedItems] = useState([])
  const [expandedItems, setExpandedItems] = useState([])

  const handleItemSelectionToggle = (event, itemId, isSelected) => {
    toggledItemRef.current[itemId] = isSelected
  }

  const buildTree = (items, parentId = null) => {
    const tree = []
    items?.forEach(item => {
      if (item?.parent === parentId) {
        const newItem = {
          ...item,
          label: item?.name,
        }
        const children = buildTree(items, item?.id)
        if (children.length) {
          newItem.children = children
        }
        tree.push(newItem)
      }
    })
    return tree
  }

  const treeData = useMemo(() => {
    return buildTree(data)
  }, [data])

  function getItemDescendantsIds(item) {
    const ids = []
    item.children?.forEach(child => {
      ids.push(child.id)
      ids.push(...getItemDescendantsIds(child))
    })
    return ids
  }

  // Function to find all parents of an item
  const getItemAncestors = itemId => {
    const ancestors = []
    const item = data.find(d => d.id === itemId)

    if (item?.parent) {
      ancestors.push(item.parent)
      ancestors.push(...getItemAncestors(item.parent))
    }

    return ancestors
  }

  // Function to determine which items should be expanded
  const calculateExpandedItems = selectedItemIds => {
    const itemsToExpand = new Set([...expandedItems]) // Keep manually expanded items

    selectedItemIds.forEach(selectedId => {
      const selectedItem = data.find(d => d.id === selectedId)

      if (selectedItem?.parent) {
        // If the selected item has a parent, check if the parent is also selected
        const isParentSelected = selectedItemIds.includes(selectedItem.parent)

        if (!isParentSelected) {
          // If the parent is not selected, expand all ancestors
          const ancestors = getItemAncestors(selectedId)
          ancestors.forEach(ancestorId => itemsToExpand.add(ancestorId))
        }
      }
    })

    return Array.from(itemsToExpand)
  }

  const handleSelectedItemsChange = (event, newSelectedItems) => {
    if (onlyParent) {
      setSelectedItems(newSelectedItems)
      const filteredItems = data.filter(item => newSelectedItems.includes(item.id))
      onSelectedItemsChange(filteredItems)
      toggledItemRef.current = {}

      // Calculate and set expanded items
      const newExpandedItems = calculateExpandedItems(newSelectedItems)
      setExpandedItems(newExpandedItems)
      return
    }

    setSelectedItems(newSelectedItems)
    const itemsToSelect = []
    const itemsToUnSelect = {}

    Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
      const item = apiRef.current.getItem(itemId)
      if (isSelected) {
        itemsToSelect.push(...getItemDescendantsIds(item))
      } else {
        getItemDescendantsIds(item).forEach(descendantId => {
          itemsToUnSelect[descendantId] = true
        })
      }
    })

    let newSelection = Array.from(
      new Set([...newSelectedItems, ...itemsToSelect].filter(itemId => !itemsToUnSelect[itemId]))
    )

    newSelection = newSelection.filter(itemId => {
      const children = data.filter(i => i?.parent === itemId).map(i => i?.id)
      if (!children.length) return true
      return children.some(childId => newSelection.includes(childId))
    })

    const selectedSet = new Set(newSelection)
    data.forEach(item => {
      if (item?.parent && selectedSet.has(item?.id)) {
        selectedSet.add(item?.parent)
      }
    })

    newSelection = Array.from(selectedSet)
    setSelectedItems(newSelection)

    const filteredItems = data.filter(item => newSelection.includes(item?.id))
    onSelectedItemsChange(filteredItems)
    toggledItemRef.current = {}

    // Calculate and set expanded items
    const newExpandedItems = calculateExpandedItems(newSelection)
    setExpandedItems(newExpandedItems)
  }

  const handleExpandedItemsChange = (event, itemIds) => {
    setExpandedItems(itemIds)
  }

  useEffect(() => {
    if (categories) {
      const categoriesIds = categories.map(category => category.id)
      setSelectedItems(categoriesIds)

      // Calculate and set expanded items based on selected categories
      const newExpandedItems = calculateExpandedItems(categoriesIds)
      setExpandedItems(newExpandedItems)
    }
  }, [categories])

  return (
    <Box sx={{ minHeight: 352, minWidth: 290 }}>
      <RichTreeView
        className={styles.label}
        multiSelect
        checkboxSelection
        apiRef={apiRef}
        items={treeData}
        selectedItems={selectedItems}
        expandedItems={expandedItems}
        onSelectedItemsChange={handleSelectedItemsChange}
        onItemSelectionToggle={handleItemSelectionToggle}
        onExpandedItemsChange={handleExpandedItemsChange}
      />
    </Box>
  )
}

export default MultiSelectedTree
